/**
 * Normalizes an angle to the interval [-Pi, Pi]
 * @param angle The angle to normalize
 */
const normalize = (angle: number): number => {
  return (
    ((angle + (Math.PI % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) -
    Math.PI
  )
}

/**
 * Returns the path bits to border the specified vertex from the left.
 * It assumes you're on the right point to start.
 * @param p1 The vertex coords
 * @param a1 The angle of the line leading to the vertex
 * @param a2 The angle of the line exiting from the vertex
 * @param r The radius
 */
const getBitsForAngle = (
  p1: number[],
  a1: number,
  a2: number,
  r: number
): string => {
  const d = normalize(Math.PI + a1 - a2)
  if (d < 0) {
    const ap1 = a1 + Math.PI / 2
    const ap2 = a2 + Math.PI / 2
    return [
      'L',
      p1[0] + Math.cos(ap1) * r,
      p1[1] + Math.sin(ap1) * r,
      'A',
      r,
      r,
      0,
      0,
      0,
      p1[0] + Math.cos(ap2) * r,
      p1[1] + Math.sin(ap2) * r
    ].join(' ')
  } else {
    const m = normalize(a1 + (a2 - a1) / 2) + (Math.PI / 2) * (a2 > a1 ? 1 : -1)
    let rr = r / Math.sin(d / 2)
    if (rr > 10 * r) rr = r // For impossible angles, just go with r
    return ['L', p1[0] + Math.cos(m) * rr, p1[1] + Math.sin(m) * rr].join(' ')
  }
}

/**
 * Generates a path with rounded corners around a given line
 * @param points The list of points in the line
 * @param r The radius to keep from the line
 */
const ThickLine = (points: number[][], r: number): string => {
  if (!points || points.length === 0) return null
  if (points.length === 1) {
    // Only one point? Just make a circle
    const p = points[0]
    return [
      'M',
      p[0] + r,
      p[1],
      'A',
      r,
      r,
      0,
      0,
      0,
      p[0] - r,
      p[1],
      'A',
      r,
      r,
      0,
      0,
      0,
      p[0] + r,
      p[1]
    ].join(' ')
  }

  // Determine the angle of each segment (eg: angle n is the angle of the line from points[n] to points[n+1])
  const angles = []
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i]
    const p2 = points[i + 1]
    angles.push(Math.atan2(p2[1] - p1[1], p2[0] - p1[0]))
  }

  // Start building bit by bit
  const bits = []

  // Add the start semicircle
  const p1 = points[0]
  const a1 = angles[0] + Math.PI / 2
  bits.push('M', p1[0] - Math.cos(a1) * r, p1[1] - Math.sin(a1) * r)
  bits.push(
    'A',
    r,
    r,
    0,
    0,
    0,
    p1[0] + Math.cos(a1) * r,
    p1[1] + Math.sin(a1) * r
  )

  // Add each segment's parallell on one side
  for (let i = 1; i < points.length - 1; i++) {
    bits.push(getBitsForAngle(points[i], angles[i - 1], angles[i], r))
  }

  // Add the end semicircle
  const pN = points[points.length - 1]
  const aN = angles[points.length - 2] + Math.PI / 2
  bits.push('L', pN[0] + Math.cos(aN) * r, pN[1] + Math.sin(aN) * r)
  bits.push(
    'A',
    r,
    r,
    0,
    0,
    0,
    pN[0] - Math.cos(aN) * r,
    pN[1] - Math.sin(aN) * r
  )

  // Add each segment's parallel on the other side
  for (let i = points.length - 2; i > 0; i--) {
    bits.push(
      getBitsForAngle(
        points[i],
        angles[i] + Math.PI,
        angles[i - 1] + Math.PI,
        r
      )
    )
  }

  // Complete the last segment
  bits.push('L', p1[0] - Math.cos(a1) * r, p1[1] - Math.sin(a1) * r)

  return bits.join(' ')
}

export default ThickLine
